/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in mirror-propagation-diagram.css. */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent } from "react"

import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { cardRingClassName } from "@/routes/-components/home-new/homeNewStyles"

import { DiagramTooltip } from "./diagram-tooltip"
import { mirrorPropagationSteps } from "./forge-diagram-steps"

import "./mirror-propagation-diagram.css"

// "Mirror Propagation": a push lands on the AU mirror, is forwarded to the EU
// home cell, accepted and acknowledged, then broadcast by git-sync to the IN
// and US mirrors. One 4.8s phase per step, looping. Both scenes are authored in
// fixed scene pixels and scaled to the figure's width; the desktop scene adds
// a camera that frames each actor as it enters.

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

const PHASE = 4.8
const DURATION = mirrorPropagationSteps.length * PHASE
/** Latest time the timeline holds; every slot window excludes its end time. */
const LAST_TIME = DURATION - 0.01
/**
 * A step's settled frame sits this far before its end: every reveal inside
 * the step has finished, and pulses that fade out at the boundary are still
 * fully shown.
 */
const SETTLE_LEAD = 0.5
const MOBILE_BREAKPOINT = 560

function phaseStart(index: number) {
  return index * PHASE
}

interface Phase {
  start: number
  end: number
  title: string
  caption: string
}

const PHASES: Phase[] = mirrorPropagationSteps.map((step, index) => ({
  ...step,
  start: phaseStart(index),
  end: phaseStart(index + 1),
}))

function settledTime(index: number) {
  return phaseStart(index + 1) - SETTLE_LEAD
}

function phaseIndexAt(t: number) {
  return Math.min(PHASES.length - 1, Math.max(0, Math.floor(t / PHASE)))
}

// Hover explanations for each actor (see DiagramTooltip).
const TIPS = {
  pushClient: {
    title: "Push client",
    body: "Git invokes the Entire remote helper, which sends this complete push to the Australian mirror.",
  },
  fetchClient: {
    title: "Fetch client",
    body: "This separate client fetches from Australia without consulting the European home cell.",
  },
  AU: {
    title: "AU · Mirror",
    body: "Both client operations enter through Australia. Fetches stay local; complete pushes are forwarded to the home cell.",
  },
  EU: {
    title: "EU · Home Cell",
    body: "The European home cell is authoritative for this repository and decides whether a forwarded push is accepted.",
  },
  IN: {
    title: "IN · Mirror",
    body: "The India mirror also keeps serving its previous complete state until it converges from Europe.",
  },
  US: {
    title: "US · Mirror",
    body: "The US mirror serves its previous complete state until git-sync applies the new ref state.",
  },
} as const

type Tip = (typeof TIPS)[keyof typeof TIPS]

/** Tooltip attributes for an actor; none while it hasn't entered yet. */
function tipProps(tip: Tip, shown = true) {
  return shown ? { "data-tip-title": tip.title, "data-tip": tip.body } : {}
}

const STALE_SHA = "a91e"
const NEW_SHA = "3f2a"

// ---------------------------------------------------------------------------
// Easing and reveal helpers
// ---------------------------------------------------------------------------

function clamp(x: number) {
  return x < 0 ? 0 : x > 1 ? 1 : x
}

function easeOut(x: number) {
  return 1 - Math.pow(1 - x, 3)
}

function riseAt(t: number, start: number, fade: number) {
  return easeOut(clamp((t - start) / fade))
}

function pulse(t: number, start: number, end: number, fade: number) {
  if (t <= start || t >= end) return 0
  return easeOut(Math.min((t - start) / fade, (end - t) / fade, 1))
}

/** Solves cubic-bezier(x1, y1, x2, y2) at progress `t` with a few Newton steps. */
function bezierY(x1: number, y1: number, x2: number, y2: number, t: number) {
  let u = t
  for (let i = 0; i < 6; i++) {
    const a = 1 - u
    const bx = 3 * a * a * u * x1 + 3 * a * u * u * x2 + u * u * u - t
    const dbx = 3 * a * a * x1 + 6 * a * u * (x2 - x1) + 3 * u * u * (1 - x2) || 1e-6
    u = clamp(u - bx / dbx)
  }
  const a = 1 - u
  return 3 * a * a * u * y1 + 3 * a * u * u * y2 + u * u * u
}

// Wire draw-in: 900ms on cubic-bezier(.22,1,.36,1).
const DRAW = 0.9

function draw(t: number, start: number) {
  return bezierY(0.22, 1, 0.36, 1, clamp((t - start) / DRAW))
}

function hiddenPct(t: number, start: number) {
  return ((1 - draw(t, start)) * 100).toFixed(2)
}

const clipDown = (t: number, start: number) => `inset(0 0 ${hiddenPct(t, start)}% 0)`
const clipUp = (t: number, start: number) => `inset(${hiddenPct(t, start)}% 0 0 0)`
const clipRight = (t: number, start: number) => `inset(0 ${hiddenPct(t, start)}% 0 0)`
const clipLeft = (t: number, start: number) => `inset(0 0 0 ${hiddenPct(t, start)}%)`

// One dot travels each drawn wire, repeating every 1.1s.
const DOT_PERIOD = 1.1

function dotFrac(t: number) {
  return (t % DOT_PERIOD) / DOT_PERIOD
}

/**
 * A point along a bent wire (down, corner, across to the left, corner, down),
 * walked at one constant speed. p=0 is the client end, p=1 the AU end.
 */
function bendPos(p: number, h1: number, w: number, h2: number, dropX: number, landX: number) {
  const total = h1 + w + h2
  const f1 = h1 / total
  const f2 = (h1 + w) / total
  if (p <= f1) return { x: dropX, y: 158 + (p / f1) * h1 }
  if (p <= f2) return { x: dropX - 16 - ((p - f1) / (f2 - f1)) * w, y: 158 + h1 + 16 }
  return { x: landX, y: 158 + h1 + 32 + ((p - f2) / (1 - f2)) * h2 }
}

// ---------------------------------------------------------------------------
// Status board: type out on first appearance (78ms/char), split-flap when a
// reading replaces another (330ms spin, 45ms stagger), and a slow re-flip plus
// glow for readings that describe work still in progress.
// ---------------------------------------------------------------------------

const MS_PER_CHAR = 0.078
const FLIP_STAGGER = 0.045
const FLIP_SPIN = 0.33
const FLIP_TICK = 0.055
const FLIP_GLYPHS = "abcdefghijklmnopqrstuvwxyz0123456789"
const REST_HOLD = 0.9
const REST_CYCLE = 4.2
const RESTLESS = new Set(["forwarding", "receiving pack", "receiving", "syncing"])

interface SlotWindow {
  start: number
  end: number
  text: string
  hash?: string
}

interface SlotState {
  text: string
  glow: number
  target: string | null
  hash: string
  hashOpacity: number
}

function typeOutFrame(text: string, elapsed: number) {
  const typed = Math.floor(elapsed / MS_PER_CHAR)
  if (typed >= text.length) return text
  return `${text.slice(0, Math.max(0, typed))}█`
}

function flipSpan(length: number) {
  return (length - 1) * FLIP_STAGGER + FLIP_SPIN
}

function splitFlapFrame(text: string, previous: string, elapsed: number) {
  let prev = previous
  if (prev.length < text.length) prev = " ".repeat(text.length - prev.length) + prev
  else if (prev.length > text.length) prev = prev.slice(prev.length - text.length)
  let out = ""
  for (let i = 0; i < text.length; i++) {
    const start = i * FLIP_STAGGER
    if (elapsed < start) out += prev.charAt(i) || " "
    else if (elapsed < start + FLIP_SPIN) {
      const tick = Math.floor((elapsed - start + i * 0.007) / FLIP_TICK)
      out += FLIP_GLYPHS.charAt(tick % FLIP_GLYPHS.length)
    } else out += text.charAt(i)
  }
  return out
}

function glowOpacity(sinceLead: number) {
  const p = (sinceLead % REST_CYCLE) / REST_CYCLE
  return 0.7 + 0.3 * Math.cos(2 * Math.PI * p)
}

function statusFrame(text: string, previous: string | null, elapsed: number, hold: number) {
  let lead = 0
  if (previous === null) {
    lead = text.length * MS_PER_CHAR
    if (elapsed < lead) return { text: typeOutFrame(text, elapsed), glow: 1, lead }
  } else if (previous !== text) {
    lead = flipSpan(text.length)
    if (elapsed < lead) return { text: splitFlapFrame(text, previous, elapsed), glow: 1, lead }
  }
  if (!RESTLESS.has(text)) return { text, glow: 1, lead }
  const waitFor = lead + REST_HOLD
  if (elapsed < waitFor) return { text, glow: 1, lead }
  const span = flipSpan(text.length)
  const sinceLead = elapsed - waitFor
  const passStart = waitFor + Math.floor(sinceLead / REST_CYCLE) * REST_CYCLE
  if (passStart + span > hold) return { text, glow: 1, lead }
  const glow = glowOpacity(sinceLead)
  if (elapsed < passStart + span) {
    return { text: splitFlapFrame(text, text, elapsed - passStart), glow, lead }
  }
  return { text, glow, lead }
}

/**
 * A slot's reading at time `t`. A window that starts where the previous one
 * ended flips from it; a reappearance after a gap types out from nothing.
 */
function slotFrame(t: number, windows: SlotWindow[]): SlotState {
  const index = windows.findIndex((w) => t >= w.start && t < w.end)
  const active = windows[index]
  if (!active) return { text: "", glow: 0, target: null, hash: "", hashOpacity: 0 }
  const prevWindow = index > 0 ? windows[index - 1] : undefined
  const contiguous = prevWindow && Math.abs(prevWindow.end - active.start) < 0.001
  const elapsed = t - active.start
  const frame = statusFrame(
    active.text,
    contiguous ? prevWindow.text : null,
    elapsed,
    active.end - active.start,
  )
  return {
    text: frame.text,
    glow: frame.glow,
    target: active.text,
    hash: active.hash ?? "",
    hashOpacity: active.hash ? riseAt(elapsed, frame.lead, 0.2) : 0,
  }
}

/** Wire labels show the active window's text as is. */
function plainSlot(t: number, windows: SlotWindow[]) {
  return windows.find((w) => t >= w.start && t < w.end)?.text ?? ""
}

const AU_STATUS: SlotWindow[] = [
  { start: phaseStart(0), end: phaseStart(1), text: "served locally" },
  { start: phaseStart(1), end: phaseStart(2), text: "forwarding", hash: NEW_SHA },
  { start: phaseStart(2), end: phaseStart(3), text: "local ref updated" },
  { start: phaseStart(3), end: phaseStart(4), text: "receiving pack" },
]
const HOME_STATUS: SlotWindow[] = [
  { start: phaseStart(1), end: phaseStart(2), text: "receiving", hash: NEW_SHA },
  { start: phaseStart(2), end: phaseStart(3), text: "accepted" },
  { start: phaseStart(3), end: phaseStart(4), text: "receiving pack" },
]
const MIRRORS_STATUS: SlotWindow[] = [
  { start: phaseStart(2), end: phaseStart(4), text: "syncing" },
  { start: phaseStart(4), end: DURATION, text: "up to date" },
]
const PUSH_LABEL: SlotWindow[] = [
  { start: phaseStart(1), end: phaseStart(2), text: "push" },
  { start: phaseStart(2), end: phaseStart(3), text: "ack" },
]
const FETCH_LABEL: SlotWindow[] = [
  { start: phaseStart(0), end: phaseStart(1), text: "fetch" },
  { start: phaseStart(3), end: phaseStart(4), text: "fetch" },
]
const PACK_TIP_LABEL: SlotWindow[] = [
  { start: phaseStart(0), end: phaseStart(1), text: "pack + tip" },
  { start: phaseStart(3), end: phaseStart(4), text: "pack + tip" },
]
const ACK_LABEL: SlotWindow[] = [{ start: phaseStart(4), end: DURATION, text: "ack" }]
const AU_HOME_LABEL: SlotWindow[] = [
  { start: phaseStart(1), end: phaseStart(2), text: "complete push" },
  { start: phaseStart(2), end: phaseStart(3), text: "accepted" },
]
const SYNC_LABEL: SlotWindow[] = [
  { start: phaseStart(2), end: DURATION, text: "git sync · mirror propagation" },
]
const AU_BADGE: SlotWindow[] = [
  { start: phaseStart(0), end: phaseStart(2), text: STALE_SHA },
  { start: phaseStart(2), end: DURATION, text: NEW_SHA },
]
const MIRRORS_BADGE: SlotWindow[] = [
  { start: phaseStart(0), end: phaseStart(4), text: STALE_SHA },
  { start: phaseStart(4), end: DURATION, text: NEW_SHA },
]

// ---------------------------------------------------------------------------
// Desktop camera: frames the union of every actor that has entered so far.
// ---------------------------------------------------------------------------

const DESKTOP_W = 1922
const MOBILE_W = 960
const SCENE_H = 1082
const CAMERA_PAD = 90
const CAMERA_MAX_ZOOM = 1.8

type Box = readonly [x: number, y: number, w: number, h: number]

const CAMERA_BOXES: Record<string, { box: Box; phase: number }> = {
  fetchClient: { box: [503, 95, 262, 63], phase: 1 },
  auPanel: { box: [226, 356.5, 540, 236.5], phase: 1 },
  pushClient: { box: [226, 95, 177, 63], phase: 2 },
  homePanel: { box: [1155, 356.5, 540, 236.5], phase: 2 },
  inPanel: { box: [226, 790.5, 540, 190.5], phase: 3 },
  usPanel: { box: [1155, 790.5, 540, 190.5], phase: 3 },
}

function cameraFor(phase: number) {
  let l = Infinity
  let t = Infinity
  let r = -Infinity
  let b = -Infinity
  for (const { box, phase: entry } of Object.values(CAMERA_BOXES)) {
    if (phase < entry) continue
    const [x, y, w, h] = box
    l = Math.min(l, x)
    t = Math.min(t, y)
    r = Math.max(r, x + w)
    b = Math.max(b, y + h)
  }
  const width = r - l
  const height = b - t
  const zoom = Math.min(
    CAMERA_MAX_ZOOM,
    Math.max(
      1,
      Math.min(DESKTOP_W / (width + CAMERA_PAD * 2), SCENE_H / (height + CAMERA_PAD * 2)),
    ),
  )
  const tx = DESKTOP_W / 2 - (l + width / 2) * zoom
  const ty = SCENE_H / 2 - (t + height / 2) * zoom
  return `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px) scale(${zoom.toFixed(4)})`
}

// ---------------------------------------------------------------------------
// Per-frame values shared by both scenes
// ---------------------------------------------------------------------------

function frameValues(t: number) {
  const found = PHASES.findIndex((p) => t >= p.start && t < p.end)
  const phaseIndex = found === -1 ? PHASES.length - 1 : found
  const phase = phaseIndex + 1
  const current = PHASES[phaseIndex]!
  const f = dotFrac(t)

  const pushPhase = pulse(t, phaseStart(1), phaseStart(2), 0.4)
  const ackPhase = pulse(t, phaseStart(2), phaseStart(3), 0.4)
  const fetchCycle = Math.max(
    pulse(t, 0, phaseStart(1), 0.4),
    pulse(t, phaseStart(3), phaseStart(4), 0.4),
  )
  const fetchStart = t < phaseStart(3) ? 0 : phaseStart(3)
  const fetchShown = fetchCycle * draw(t, fetchStart)
  const ack = riseAt(t, phaseStart(4), 0.4)
  const forward =
    Math.max(pulse(t, phaseStart(1), phaseStart(2), 0.4), riseAt(t, phaseStart(4), 0.4)) *
    draw(t, phaseStart(1))
  const accept = pulse(t, phaseStart(2), phaseStart(4), 0.4)
  const sync = riseAt(t, phaseStart(2), 0.4)

  return {
    phase,
    current,
    f,
    camera: cameraFor(phase),
    progress: PHASES.map((p) => clamp((t - p.start) / (p.end - p.start))),
    textClip: clipRight(t, current.start),

    auBadge: slotFrame(t, AU_BADGE),
    homeBadge: slotFrame(t, AU_BADGE),
    mirrorsBadge: slotFrame(t, MIRRORS_BADGE),
    fetchBadge: slotFrame(t, MIRRORS_BADGE),
    auStatus: slotFrame(t, AU_STATUS),
    homeStatus: slotFrame(t, HOME_STATUS),
    mirrorsStatus: slotFrame(t, MIRRORS_STATUS),

    pushLabel: plainSlot(t, PUSH_LABEL),
    fetchLabel: plainSlot(t, FETCH_LABEL),
    packTipLabel: plainSlot(t, PACK_TIP_LABEL),
    ackLabel: plainSlot(t, ACK_LABEL),
    auHomeLabel: plainSlot(t, AU_HOME_LABEL),
    syncLabel: plainSlot(t, SYNC_LABEL),

    fetchCycle,
    fetchShown,
    fetchDownClip: clipDown(t, fetchStart),
    fetchUpClip: clipUp(t, fetchStart),

    ack,
    ackShown: ack * draw(t, phaseStart(4)),
    ackClip: clipUp(t, phaseStart(4)),

    pushLine: Math.max(pushPhase, ackPhase),
    pushPill: pulse(t, phaseStart(1), phaseStart(3), 0.4),
    pushClip: clipDown(t, phaseStart(1)),
    pushShown: pushPhase * draw(t, phaseStart(1)),
    pushAck: ackPhase,

    auHomeLine: riseAt(t, phaseStart(1), 0.4),
    auHomeClip: clipRight(t, phaseStart(1)),
    auHomePill: pulse(t, phaseStart(1), phaseStart(3), 0.4),
    forward,
    accept,

    sync,
    syncTrunkClip: clipDown(t, phaseStart(2)),
    syncUsClip: clipDown(t, phaseStart(2) + DRAW),
    syncBranchClip: clipLeft(t, phaseStart(2) + DRAW),
    syncInClip: clipDown(t, phaseStart(2) + DRAW * 2),
    syncTrunkShown: sync * draw(t, phaseStart(2)),
    syncBranchShown: sync * draw(t, phaseStart(2) + DRAW),
    syncUsShown: sync * draw(t, phaseStart(2) + DRAW),
    syncInShown: sync * draw(t, phaseStart(2) + DRAW * 2),
    syncCornerA: riseAt(t, phaseStart(2) + DRAW, 0.15),
    syncCornerB: riseAt(t, phaseStart(2) + DRAW * 2, 0.15),

    pushClientIn: phase >= 2,
    homeIn: phase >= 2,
    mirrorsIn: phase >= 3,
  }
}

type FrameValues = ReturnType<typeof frameValues>

// ---------------------------------------------------------------------------
// Scene primitives (all coordinates in scene pixels)
// ---------------------------------------------------------------------------

function at(x: number, y: number, w?: number, h?: number): CSSProperties {
  return { left: x, top: y, width: w, height: h }
}

function Wire({
  dir,
  x,
  y,
  length,
  opacity,
  clip,
}: {
  dir: "v" | "h"
  x: number
  y: number
  length: number
  opacity: number
  clip?: string
}) {
  return (
    <div
      className={`mp-wire mp-wire--${dir}`}
      style={{
        ...(dir === "v" ? at(x, y, 2, length) : at(x, y, length, 2)),
        opacity,
        clipPath: clip,
      }}
    />
  )
}

function Dot({
  dir,
  x,
  y,
  opacity,
}: {
  dir: "v" | "h" | "round"
  x: number
  y: number
  opacity: number
}) {
  const [w, h] = dir === "v" ? [5, 12] : dir === "h" ? [12, 5] : [8, 8]
  return <div className="mp-dot" style={{ ...at(x, y, w, h), opacity }} />
}

function WirePill({
  x,
  y,
  w,
  text,
  opacity,
}: {
  x: number
  y: number
  w: number
  text: string
  opacity: number
}) {
  return (
    <div className="mp-pill" style={{ ...at(x, y, w, 52), opacity }}>
      {text}
    </div>
  )
}

type ArrowDir = "down" | "up" | "right" | "left"

/** Arrowhead polygon whose apex lands on (x, y). */
function arrowPoints(dir: ArrowDir, x: number, y: number) {
  switch (dir) {
    case "down":
      return `${x - 7},${y - 11} ${x + 7},${y - 11} ${x},${y}`
    case "up":
      return `${x - 7},${y + 11} ${x + 7},${y + 11} ${x},${y}`
    case "right":
      return `${x - 11},${y - 7} ${x - 11},${y + 7} ${x},${y}`
    case "left":
      return `${x + 11},${y - 7} ${x + 11},${y + 7} ${x},${y}`
  }
}

function Arrow({ dir, x, y, opacity }: { dir: ArrowDir; x: number; y: number; opacity: number }) {
  return <polygon className="mp-arrow" points={arrowPoints(dir, x, y)} style={{ opacity }} />
}

function Corner({ d, opacity }: { d: string; opacity: number }) {
  return <path className="mp-corner" d={d} style={{ opacity }} />
}

function Sha({ slot }: { slot: SlotState }) {
  return (
    <span className="mp-sha" data-stale={slot.target === STALE_SHA ? "true" : undefined}>
      {slot.text}
    </span>
  )
}

function PinIcon() {
  return (
    <svg width="14.584" height="19" viewBox="460.208 32 14.584 19" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M467.5 51C467.5 51 474.792 44.5 474.792 39C474.792 37.1435 474.024 35.363 472.656 34.0503C471.289 32.7375 469.434 32 467.5 32C465.566 32 463.712 32.7375 462.344 34.0503C460.977 35.363 460.208 37.1435 460.208 39C460.208 44.5 467.5 51 467.5 51ZM470.104 39C470.104 39.663 469.83 40.2989 469.342 40.7678C468.853 41.2366 468.191 41.5 467.5 41.5C466.809 41.5 466.147 41.2366 465.659 40.7678C465.17 40.2989 464.896 39.663 464.896 39C464.896 38.337 465.17 37.7011 465.659 37.2322C466.147 36.7634 466.809 36.5 467.5 36.5C468.191 36.5 468.853 36.7634 469.342 37.2322C469.83 37.7011 470.104 38.337 470.104 39Z"
      />
    </svg>
  )
}

function Panel({
  x,
  y,
  w,
  home = false,
  region,
  subtitle,
  badge,
  status,
  visible = true,
}: {
  x: number
  y: number
  w: number
  home?: boolean
  region: "AU" | "EU" | "IN" | "US"
  subtitle?: string
  badge: SlotState
  status: SlotState
  visible?: boolean
}) {
  return (
    <div
      className={`mp-panel ${home ? "mp-panel--home" : ""}`}
      style={{ ...at(x, y, w), opacity: visible ? 1 : 0 }}
      {...tipProps(TIPS[region], visible)}
    >
      <div className="mp-panel__header">
        <span className="mp-panel__title">{home ? "HOME CELL" : "MIRROR"}</span>
        <span className="mp-panel__region">
          <PinIcon />
          {region}
        </span>
      </div>
      <div className={`mp-panel__body ${subtitle ? "" : "mp-panel__body--compact"}`}>
        {subtitle ? <div className="mp-panel__subtitle">{subtitle}</div> : null}
        <div className="mp-row">
          <div className="mp-row__ref">
            <span>main</span>
            <Sha slot={badge} />
          </div>
          <div className="mp-row__status">
            <span style={{ opacity: status.glow }}>{status.text}</span>
            {status.hash ? (
              <span className="mp-row__hash" style={{ opacity: status.hashOpacity }}>
                {status.hash}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function Clients({ v, pushX, fetchX }: { v: FrameValues; pushX: number; fetchX: number }) {
  return (
    <>
      <div
        className="mp-client mp-enter"
        style={{ ...at(pushX, 95, 177, 63), opacity: v.pushClientIn ? 1 : 0 }}
        {...tipProps(TIPS.pushClient, v.pushClientIn)}
      >
        <span>push</span>
        <span className="mp-sha">{NEW_SHA}</span>
      </div>
      <div className="mp-client" style={at(fetchX, 95, 262, 63)} {...tipProps(TIPS.fetchClient)}>
        <span>fetch main</span>
        <Sha slot={v.fetchBadge} />
      </div>
    </>
  )
}

function Panels({
  v,
  left,
  right,
  width,
}: {
  v: FrameValues
  left: number
  right: number
  width: number
}) {
  return (
    <>
      <Panel
        x={left}
        y={356.5}
        w={width}
        region="AU"
        subtitle="local reads · forwarded writes"
        badge={v.auBadge}
        status={v.auStatus}
      />
      <Panel
        x={right}
        y={356.5}
        w={width}
        home
        region="EU"
        subtitle="authoritative upstream"
        badge={v.homeBadge}
        status={v.homeStatus}
        visible={v.homeIn}
      />
      <Panel
        x={left}
        y={790.5}
        w={width}
        region="IN"
        badge={v.mirrorsBadge}
        status={v.mirrorsStatus}
        visible={v.mirrorsIn}
      />
      <Panel
        x={right}
        y={790.5}
        w={width}
        region="US"
        badge={v.mirrorsBadge}
        status={v.mirrorsStatus}
        visible={v.mirrorsIn}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Desktop scene (1922 x 1082) with camera
// ---------------------------------------------------------------------------

function DesktopScene({ v, scale }: { v: FrameValues; scale: number }) {
  const f = v.f
  return (
    <div
      className="mp-scene"
      style={{ width: DESKTOP_W, height: SCENE_H, transform: `scale(${scale})` }}
    >
      <div className="mp-camera" style={{ width: DESKTOP_W, height: SCENE_H, transform: v.camera }}>
        <Clients v={v} pushX={226} fetchX={503} />

        {/* Fetch cycle: fetch down into AU, pack + tip back up. */}
        <Wire
          dir="v"
          x={568.5}
          y={158}
          length={198.5}
          opacity={v.fetchCycle}
          clip={v.fetchDownClip}
        />
        <Dot dir="v" x={567} y={158 + f * 198.5 - 6} opacity={v.fetchShown} />
        <Wire
          dir="v"
          x={674.5}
          y={158}
          length={198.5}
          opacity={v.fetchCycle}
          clip={v.fetchUpClip}
        />
        <Dot dir="v" x={673} y={158 + (1 - f) * 198.5 - 6} opacity={v.fetchShown} />
        <WirePill x={590} y={193} w={171} text={v.packTipLabel} opacity={v.fetchCycle} />
        <WirePill x={514} y={261} w={111} text={v.fetchLabel} opacity={v.fetchCycle} />

        {/* Phase 5: a single ack back to the fetch client. */}
        <Wire dir="v" x={633.5} y={158} length={198.5} opacity={v.ack} clip={v.ackClip} />
        <Dot dir="v" x={632} y={158 + (1 - f) * 198.5 - 6} opacity={v.ackShown} />
        <WirePill x={549} y={232} w={171} text={v.ackLabel} opacity={v.ack} />

        {/* Push into AU, then its ack back out. */}
        <Wire dir="v" x={313.5} y={158} length={198.5} opacity={v.pushLine} clip={v.pushClip} />
        <Dot dir="v" x={312} y={158 + f * 198.5 - 6} opacity={v.pushShown} />
        <Dot dir="v" x={312} y={158 + (1 - f) * 198.5 - 6} opacity={v.pushAck} />
        <WirePill x={229} y={232} w={171} text={v.pushLabel} opacity={v.pushPill} />

        {/* AU and the home cell: forward, then accept. */}
        <Wire dir="h" x={766} y={476} length={389} opacity={v.auHomeLine} clip={v.auHomeClip} />
        <Dot dir="h" x={766 + f * 389 - 6} y={474} opacity={v.forward} />
        <Dot dir="h" x={766 + (1 - f) * 389 - 6} y={474} opacity={v.accept} />
        <WirePill x={842} y={448} w={238} text={v.auHomeLabel} opacity={v.auHomePill} />

        {/* git-sync broadcast: trunk down, US straight on, IN branches left. */}
        <Wire dir="v" x={1424} y={593} length={82.75} opacity={v.sync} clip={v.syncTrunkClip} />
        <Wire dir="h" x={515} y={691.75} length={894} opacity={v.sync} clip={v.syncBranchClip} />
        <Wire dir="v" x={1424} y={691.75} length={98.75} opacity={v.sync} clip={v.syncUsClip} />
        <Wire dir="v" x={498} y={707.75} length={82.75} opacity={v.sync} clip={v.syncInClip} />

        <svg
          className="mp-overlay"
          viewBox={`0 0 ${DESKTOP_W} ${SCENE_H}`}
          style={{ width: DESKTOP_W, height: SCENE_H }}
          aria-hidden="true"
        >
          <Corner d="M 1425 675.75 Q 1425 691.75 1409 691.75" opacity={v.syncCornerA} />
          <Corner d="M 515 691.75 Q 499 691.75 499 707.75" opacity={v.syncCornerB} />
          <Arrow dir="down" x={569.5} y={356.5} opacity={v.fetchShown} />
          <Arrow dir="down" x={314.5} y={356.5} opacity={v.pushShown} />
          <Arrow dir="up" x={675.5} y={158} opacity={v.fetchShown} />
          <Arrow dir="up" x={314.5} y={158} opacity={v.pushAck} />
          <Arrow dir="up" x={634.5} y={158} opacity={v.ackShown} />
          <Arrow dir="right" x={1155} y={476.5} opacity={v.forward} />
          <Arrow dir="left" x={766} y={476.5} opacity={v.accept} />
          <Arrow dir="down" x={1425} y={790.5} opacity={v.syncUsShown} />
          <Arrow dir="down" x={499} y={790.5} opacity={v.syncInShown} />
        </svg>

        <Dot dir="v" x={1422.5} y={593 + f * 82.75 - 6} opacity={v.syncTrunkShown} />
        <Dot dir="v" x={1422.5} y={691.75 + f * 98.75 - 6} opacity={v.syncUsShown} />
        <Dot dir="h" x={515 + (1 - f) * 894 - 6} y={689.25} opacity={v.syncBranchShown} />
        <Dot dir="v" x={496.5} y={707.75 + f * 82.75 - 6} opacity={v.syncInShown} />
        <WirePill x={723} y={666} w={478} text={v.syncLabel} opacity={v.sync} />

        <Panels v={v} left={226} right={1155} width={540} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mobile scene (960 x 1082): narrower columns, no camera. The fetch cycle has
// to travel from the fetch client (over the home column) back to AU, so its
// wires bend.
// ---------------------------------------------------------------------------

function BentWire({
  dropX,
  h1,
  w,
  h2,
  landX,
  opacity,
}: {
  dropX: number
  h1: number
  w: number
  h2: number
  landX: number
  opacity: number
}) {
  const acrossY = 158 + h1 + 16
  return (
    <>
      <Wire dir="v" x={dropX - 1} y={158} length={h1} opacity={opacity} />
      <Wire dir="h" x={dropX - 16 - w} y={acrossY - 1} length={w} opacity={opacity} />
      <Wire dir="v" x={landX - 1} y={acrossY + 16} length={h2} opacity={opacity} />
    </>
  )
}

function bentCorners(dropX: number, h1: number, w: number, landX: number) {
  const top = 158 + h1
  const acrossY = top + 16
  const turnX = dropX - 16
  const landStart = turnX - w
  return [
    `M ${dropX} ${top} Q ${dropX} ${acrossY} ${turnX} ${acrossY}`,
    `M ${landStart} ${acrossY} Q ${landX} ${acrossY} ${landX} ${acrossY + 16}`,
  ] as const
}

const FETCH_BEND = { dropX: 548.5, h1: 42, w: 299.5, h2: 124.5, landX: 217 }
const PACK_TIP_BEND = { dropX: 654.5, h1: 106, w: 339.5, h2: 60.5, landX: 283 }
const ACK_BEND = { dropX: 613.5, h1: 42, w: 331.5, h2: 124.5, landX: 250 }

function bendDot(bend: typeof FETCH_BEND, p: number) {
  const pos = bendPos(p, bend.h1, bend.w, bend.h2, bend.dropX, bend.landX)
  return { x: pos.x - 4, y: pos.y - 4 }
}

function MobileScene({ v, scale }: { v: FrameValues; scale: number }) {
  const f = v.f
  const fetchDot = bendDot(FETCH_BEND, f)
  const packTipDot = bendDot(PACK_TIP_BEND, 1 - f)
  const ackDot = bendDot(ACK_BEND, 1 - f)
  const corners = [
    { bend: FETCH_BEND, opacity: v.fetchCycle },
    { bend: PACK_TIP_BEND, opacity: v.fetchCycle },
    { bend: ACK_BEND, opacity: v.ack },
  ]

  return (
    <div
      className="mp-scene"
      style={{ width: MOBILE_W, height: SCENE_H, transform: `scale(${scale})` }}
    >
      <Clients v={v} pushX={154} fetchX={483} />

      <BentWire {...FETCH_BEND} opacity={v.fetchCycle} />
      <Dot dir="round" {...fetchDot} opacity={v.fetchCycle} />
      <BentWire {...PACK_TIP_BEND} opacity={v.fetchCycle} />
      <Dot dir="round" {...packTipDot} opacity={v.fetchCycle} />
      <BentWire {...ACK_BEND} opacity={v.ack} />
      <Dot dir="round" {...ackDot} opacity={v.ack} />
      <WirePill x={383} y={254} w={171} text={v.packTipLabel} opacity={v.fetchCycle} />
      <WirePill x={327} y={190} w={111} text={v.fetchLabel} opacity={v.fetchCycle} />
      <WirePill x={346} y={190} w={171} text={v.ackLabel} opacity={v.ack} />

      <Wire dir="v" x={241.5} y={158} length={198.5} opacity={v.pushLine} clip={v.pushClip} />
      <Dot dir="v" x={241} y={158 + f * 198.5 - 6} opacity={v.pushShown} />
      <Dot dir="v" x={241} y={158 + (1 - f) * 198.5 - 6} opacity={v.pushAck} />
      <WirePill x={157} y={232} w={171} text={v.pushLabel} opacity={v.pushPill} />

      {/* No label between the adjacent columns; each panel's status carries it. */}
      <Wire dir="h" x={460} y={476} length={40} opacity={v.auHomeLine} clip={v.auHomeClip} />
      <Dot dir="h" x={460 + f * 40 - 6} y={474} opacity={v.forward} />
      <Dot dir="h" x={460 + (1 - f) * 40 - 6} y={474} opacity={v.accept} />

      <Wire dir="v" x={709} y={593} length={82.75} opacity={v.sync} clip={v.syncTrunkClip} />
      <Wire dir="h" x={266} y={691.75} length={428} opacity={v.sync} clip={v.syncBranchClip} />
      <Wire dir="v" x={709} y={691.75} length={98.75} opacity={v.sync} clip={v.syncUsClip} />
      <Wire dir="v" x={249} y={707.75} length={82.75} opacity={v.sync} clip={v.syncInClip} />

      <svg
        className="mp-overlay"
        viewBox={`0 0 ${MOBILE_W} ${SCENE_H}`}
        style={{ width: MOBILE_W, height: SCENE_H }}
        aria-hidden="true"
      >
        {corners.flatMap(({ bend, opacity }) =>
          bentCorners(bend.dropX, bend.h1, bend.w, bend.landX).map((d) => (
            <Corner key={d} d={d} opacity={opacity} />
          )),
        )}
        <Corner d="M 710 675.75 Q 710 691.75 694 691.75" opacity={v.syncCornerA} />
        <Corner d="M 266 691.75 Q 250 691.75 250 707.75" opacity={v.syncCornerB} />
        <Arrow dir="down" x={217} y={356.5} opacity={v.fetchShown} />
        <Arrow dir="down" x={242.5} y={356.5} opacity={v.pushShown} />
        <Arrow dir="up" x={654.5} y={158} opacity={v.fetchShown} />
        <Arrow dir="up" x={242.5} y={158} opacity={v.pushAck} />
        <Arrow dir="up" x={613.5} y={158} opacity={v.ackShown} />
        <Arrow dir="right" x={500} y={476.5} opacity={v.forward} />
        <Arrow dir="left" x={460} y={476.5} opacity={v.accept} />
        <Arrow dir="down" x={710} y={790.5} opacity={v.syncUsShown} />
        <Arrow dir="down" x={250} y={790.5} opacity={v.syncInShown} />
      </svg>

      <Dot dir="v" x={707.5} y={593 + f * 82.75 - 6} opacity={v.syncTrunkShown} />
      <Dot dir="v" x={707.5} y={691.75 + f * 98.75 - 6} opacity={v.syncUsShown} />
      <Dot dir="h" x={266 + (1 - f) * 428 - 6} y={689.25} opacity={v.syncBranchShown} />
      <Dot dir="v" x={247.5} y={707.75 + f * 82.75 - 6} opacity={v.syncInShown} />
      <WirePill x={241} y={666} w={478} text={v.syncLabel} opacity={v.sync} />

      <Panels v={v} left={40} right={500} width={420} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * How a push propagates across Entire's regions: the AU mirror forwards it to
 * the EU home cell, which accepts it and broadcasts it to every mirror by
 * git-sync. Plays only while on screen; reduced motion opens on the converged
 * final step and moves between settled steps on request (styles: `.mp-root`).
 */
export function MirrorPropagationDiagram() {
  const rootRef = useRef<HTMLElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const previousFrameRef = useRef<number | null>(null)
  const [time, setTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [hasPickedStep, setHasPickedStep] = useState(false)
  const width = useElementWidth(rootRef)
  const isVisible = useIsVisible(rootRef)
  const prefersReducedMotion = usePrefersReducedMotion()
  // Reduced motion opens on the settled final step until the reader steps.
  const displayTime = prefersReducedMotion && !hasPickedStep ? settledTime(PHASES.length - 1) : time
  const v = useMemo(() => frameValues(displayTime), [displayTime])

  const isMobile = width < MOBILE_BREAKPOINT
  const scale = Math.min(1, width / (isMobile ? MOBILE_W : DESKTOP_W))
  const stageHeight = Math.round(SCENE_H * scale)

  useEffect(() => {
    if (!isVisible || !isPlaying || prefersReducedMotion) {
      previousFrameRef.current = null
      return
    }

    const animate = (timestamp: number) => {
      const previousFrame = previousFrameRef.current ?? timestamp
      const delta = Math.min(0.06, (timestamp - previousFrame) / 1000)
      previousFrameRef.current = timestamp
      setTime((currentTime) => {
        const nextTime = currentTime + delta
        return nextTime >= DURATION ? 0 : nextTime
      })
      animationFrameRef.current = window.requestAnimationFrame(animate)
    }

    animationFrameRef.current = window.requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      previousFrameRef.current = null
    }
  }, [isPlaying, isVisible, prefersReducedMotion])

  const paused = prefersReducedMotion || !isPlaying
  const phaseIndex = v.phase - 1

  /** Paused, a step opens on its settled frame; playing, it plays from its start. */
  function goToStep(index: number) {
    setHasPickedStep(true)
    setTime(paused ? settledTime(index) : phaseStart(index))
  }

  function changeTime(event: ChangeEvent<HTMLInputElement>) {
    const value = Math.min(LAST_TIME, Math.max(0, Number(event.currentTarget.value)))
    // Under reduced motion the slider steps between settled steps.
    if (prefersReducedMotion) goToStep(phaseIndexAt(value))
    else setTime(value)
  }

  function skip() {
    goToStep((phaseIndex + 1) % PHASES.length)
  }

  function togglePlaying() {
    // Resuming from a settled step replays it rather than jumping past it.
    if (!isPlaying && time === settledTime(phaseIndex)) setTime(phaseStart(phaseIndex))
    setIsPlaying(!isPlaying)
  }

  const stepLabel = `${String(v.phase).padStart(2, "0")} / ${String(PHASES.length).padStart(2, "0")}`

  return (
    <figure
      ref={rootRef}
      className={`mp-root not-prose my-10 overflow-hidden rounded-xl font-sans ${cardRingClassName}`}
      aria-label="Mirror propagation: a push forwarded from the AU mirror to the EU home cell, accepted, then synced to every mirror"
    >
      <div className="flex h-10 items-center gap-2 border-b border-default px-4 text-xs leading-4">
        <span className="shrink-0 text-muted">Mirror propagation</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-default">
          Fetches and clones are served by any cell
        </span>
      </div>

      <div className="mp-stage" style={{ height: stageHeight }} aria-hidden="true">
        {isMobile ? <MobileScene v={v} scale={scale} /> : <DesktopScene v={v} scale={scale} />}
      </div>

      <div className="mp-timeline">
        <div className="mp-timeline__segments" aria-hidden="true">
          {v.progress.map((progress, index) => (
            <span key={index}>
              <span style={{ width: `${progress * 100}%` }} />
            </span>
          ))}
        </div>
        <input
          type="range"
          min={0}
          max={LAST_TIME}
          step={prefersReducedMotion ? PHASE : 0.01}
          value={prefersReducedMotion ? phaseStart(phaseIndex) : displayTime}
          onChange={changeTime}
          aria-label="Animation progress"
          aria-valuetext={`Step ${v.phase} of ${PHASES.length}: ${v.current.title}`}
        />
      </div>

      <figcaption className="flex flex-col gap-1.5 px-4 pt-3 pb-4 sm:px-5">
        <div className="flex min-h-6 items-center gap-2 text-xs leading-4">
          <span className="shrink-0 font-mono text-muted tabular-nums">{stepLabel}</span>
          <span
            className="min-w-0 flex-1 truncate font-semibold text-default"
            style={{ clipPath: v.textClip }}
          >
            {v.current.title}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              className="mp-control"
              onClick={togglePlaying}
              disabled={prefersReducedMotion}
              aria-label={isPlaying ? "Pause animation" : "Play animation"}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                {isPlaying && !prefersReducedMotion ? (
                  <path d="M3 2h4v12H3zM9 2h4v12H9z" fill="currentColor" />
                ) : (
                  <path d="M4 2l10 6-10 6z" fill="currentColor" />
                )}
              </svg>
            </button>
            <button
              type="button"
              className="mp-control"
              onClick={skip}
              aria-label="Skip to next step"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 2l8 6-8 6z" fill="currentColor" />
                <rect x="12" y="2" width="2" height="12" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-sm leading-5 text-muted" style={{ clipPath: v.textClip }}>
          {v.current.caption}
        </p>
      </figcaption>
      <DiagramTooltip rootRef={rootRef} />
    </figure>
  )
}
